import { AxiosError } from 'axios';
import * as React from 'react';

import useProgressBar from 'src/CustomHooks/useProgressBar';
import AttributionContainer from 'src/SharedComponents/AttributionContainer';
import Button from 'src/SharedComponents/Button';
import ButtonContainer from 'src/SharedComponents/ButtonContainer';
import ContentWrapper from 'src/SharedComponents/ContentWrapper';
import ErrorToast from 'src/SharedComponents/ErrorToast';
import LoadingIndicator from 'src/SharedComponents/LoadingIndicator';
import MultipleChoiceQuestion from 'src/SharedComponents/MultipleChoiceQuestion';
import ProgressBar from 'src/SharedComponents/ProgressBar';
import QuestionList from 'src/SharedComponents/QuestionList';

import { deleteAnswer, saveAnswer, submitForm } from 'src/service';
import { SessionContext } from 'src/Views/SessionContext';

const PROGRESS_ANSWERS = ["Much worse", "A bit worse", "About the same", "A bit better", "Much better"];
const HELPFUL_ANSWERS = ["Not at all", "Only a little", "Quite a lot", "A great deal"];
const DIFFICULTIES_ANSWERS = ["No", "Yes \u2013 minor difficulties", "Yes \u2013 definite difficulties", "Yes \u2013 severe difficulties"];
const QUESTION_IDS = [26, 27, 28, 29, 30, 31, 32, 33, 34];

const SdqFormPage2 = () => {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const { dispatch, state } = React.useContext(SessionContext);
    const [hasDifficulties, setHasDifficulties] = React.useState(false);
    const { answered, numberAnswered, updateAnswered, getAnswered, includedInArray, ERROR_SAVING_MESSAGE } = useProgressBar();
    const questionRefs = QUESTION_IDS.map(questionId => React.useRef(null as unknown as HTMLLIElement));

    const checkUnauthorised = (serverError: AxiosError) => {
        if (serverError.response && serverError.response.data.code === 401) {
            dispatch({ type: "clearWebToken", payload: undefined });
        }
    }

    const onDifficultiesAnswerChange = (questionId: number, answer: string, itemIndex?: number) => {
        setError("");
        saveAnswer(questionId, answer, state.webToken, () => {
            updateAnswered(questionId, !!answer, itemIndex !== undefined ? itemIndex : -1);
        }, handleError)
        setHasDifficulties(answer !== "" && answer !== "No");
    };

    const handlePromiseError = (reject: () => void) => (serverError: AxiosError, questionId: number) => {
        reject();
        handleError(serverError, questionId);
    };

    const handleError = (serverError: AxiosError, questionId: number) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE);
        updateAnswered(questionId, false, -1);
    };

    const handleSubmissionError = (serverError: AxiosError) => {
        checkUnauthorised(serverError);
        setError(ERROR_SAVING_MESSAGE);
    };

    const onAnswerChange = (questionId: number, answer: string, itemIndex?: number) => {
        setError("");
        saveAnswer(questionId, answer, state.webToken, () => {
            updateAnswered(questionId, !!answer, itemIndex !== undefined ? itemIndex : -1);
        }, handleError)
    };

    const onDisabledClick = () => {
        const firstUnansweredIndex = QUESTION_IDS.findIndex(requiredAnswerId => !includedInArray(answered, requiredAnswerId));
        if (firstUnansweredIndex > -1) {
            questionRefs[firstUnansweredIndex].current.scrollIntoView({ behavior: 'smooth', block: 'start' })
            setError("Please answer all questions");
        }
    };

    const goToNextPage = () => {
        setLoading(true);
        const promises = [];
        if (!hasDifficulties) {
            // delete questions potentially answered
            for (let i = 29; i <= 34; i++) {
                if (includedInArray(answered, i)) {
                    promises.push(new Promise((resolve, reject) => deleteAnswer(i, state.webToken, resolve, handlePromiseError(reject))));
                }
            }
        }
        Promise.allSettled(promises).then(() => {
            submitForm(state.webToken, () => {
                dispatch({ type: "setPage", payload: { formId: 1, page: 3 } });
            }, handleSubmissionError);
        });
    };

    const noDifficultiesAnswered = () => {
        return includedInArray(answered, 26) && includedInArray(answered, 27) && includedInArray(answered, 28);
    };

    return <ContentWrapper>
        <QuestionList>
            <MultipleChoiceQuestion checkedItem={getAnswered(26)} refProp={questionRefs[0]} question="Since coming to the clinic are your problems:" questionId={26} answers={PROGRESS_ANSWERS} onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion
                refProp={questionRefs[1]}
                checkedItem={getAnswered(27)}
                question="Has coming to the clinic been helpful in other ways, e.g. providing information or making the problems more bearable?"
                questionId={27}
                answers={HELPFUL_ANSWERS}
                onAnswerChange={onAnswerChange}
            />
            <MultipleChoiceQuestion
                refProp={questionRefs[2]}
                checkedItem={getAnswered(28)}
                question="Over the last month have you had difficulties in one or more of the following areas: emotions, concentration, behaviour or being able to get on with other people?"
                questionId={28}
                answers={DIFFICULTIES_ANSWERS}
                onAnswerChange={onDifficultiesAnswerChange}
            />
            <MultipleChoiceQuestion
                hidden={!hasDifficulties}
                checkedItem={getAnswered(29)}
                refProp={questionRefs[3]}
                question="Do the difficulties upset or distress you?"
                questionId={29}
                answers={HELPFUL_ANSWERS}
                onAnswerChange={onAnswerChange} />
            <MultipleChoiceQuestion
                hidden={!hasDifficulties}
                checkedItem={getAnswered(30)}
                refProp={questionRefs[4]}
                alternateColour={true}
                question="Do the difficulties interfere with your life in the following areas?"
                subheading="Homelife"
                questionId={30}
                answers={HELPFUL_ANSWERS}
                onAnswerChange={onAnswerChange}
            />
            <MultipleChoiceQuestion
                hidden={!hasDifficulties}
                checkedItem={getAnswered(31)}
                refProp={questionRefs[5]}
                alternateColour={true}
                answers={HELPFUL_ANSWERS}
                question=""
                questionId={31}
                onAnswerChange={onAnswerChange}
                subheading="Friendships"
            />
            <MultipleChoiceQuestion
                hidden={!hasDifficulties}
                checkedItem={getAnswered(32)}
                refProp={questionRefs[6]}
                alternateColour={true}
                answers={HELPFUL_ANSWERS}
                question=""
                questionId={32}
                onAnswerChange={onAnswerChange}
                subheading="Classroom learning"
            />
            <MultipleChoiceQuestion
                hidden={!hasDifficulties}
                checkedItem={getAnswered(33)}
                refProp={questionRefs[7]}
                alternateColour={true}
                answers={HELPFUL_ANSWERS}
                question=""
                questionId={33}
                onAnswerChange={onAnswerChange}
                subheading="Leisure activities"
            />
            <MultipleChoiceQuestion
                hidden={!hasDifficulties}
                checkedItem={getAnswered(34)}
                refProp={questionRefs[8]}
                answers={HELPFUL_ANSWERS}
                question="Do your difficulties make it harder for those around you (family, friends, teachers, etc.)"
                questionId={34}
                onAnswerChange={onAnswerChange}
            />
        </QuestionList>
        <ButtonContainer>
            {loading && <LoadingIndicator />}
            {!loading && <Button text="Save answers" onClick={goToNextPage} onDisabledClick={onDisabledClick} disabled={hasDifficulties ? numberAnswered !== 9 : !noDifficultiesAnswered()} />}
        </ButtonContainer>
        <ProgressBar length={hasDifficulties ? 9 : 3} progress={numberAnswered} />
        <ErrorToast role="alert" aria-atomic="true" className={error ? "sliding" : "hidden"}>{error}</ErrorToast>
        <AttributionContainer>
            The Strengths and Difficulties Questionnaire (SDQ) is copyright of Youthinmind Limited
        </AttributionContainer>
    </ContentWrapper>;
};

export default SdqFormPage2;



// WEBPACK FOOTER //
// ./src/Forms/sdq/SdqFormPage2.tsx